import type { MyAxiosResponseData, UserAddressResponseData } from '../sdk/types';
import { AbstractApi } from './Api';

export interface UploadAvatarPreSignBody {
  type?: string;
  name?: string;
  category?: 'avatar';
  date_part?: number;
}

// Device item data structure from API response
export interface DeviceItem {
  device_id: string;
  device_type: number; // 1: mobile, 2: desktop
  app_version: string;
  device_name: string;
  os_version: string;
  ip: string;
  activity_time: number;
  country: string;
  province: string;
}

// Devices list response data structure
export interface DevicesResponseData {
  list: DeviceItem[];
}

export class CommonApi extends AbstractApi {
  async getShareData(params: { u: string }) {
    const { data } = await this.request<{
      data: {
        user: {
          user_id: string;
          chat_id: string;
          name: string;
          avatar: string;
        };
        group: {
          gid: string;
          group_name: string;
          group_head: string;
          is_encryption: number;
          member_num: number;
        };
      };
    }>({
      url: '/api/v1/user/share',
      method: 'GET',
      params,
      encrypt_body: true,
    });
    return data;
  }
  async getShareUrl(body: { group_id?: string; user_id?: string }) {
    const { data } = await this.request<{ data: { url?: string } }>({
      url: '/api/v1/user/share',
      method: 'POST',
      body,
      encrypt_body: true,
    });
    return data;
  }
  async setQrCode(body: { value: string }) {
    return this.request<{ data: { key: string } }>({
      url: '/api/v1/qrcode/set',
      method: 'POST',
      body,
      encrypt_body: true,
    });
  }
  async getQrCodeData(params: { key: string }) {
    const { data } = await this.request<{
      data: {
        value: string;
      };
    }>({
      url: '/api/v1/qrcode',
      method: 'GET',
      params,
      encrypt_body: true,
    });
    return data;
  }
  getUploadAvatarPreSign(body: UploadAvatarPreSignBody) {
    return this.request<{
      code: number;
      message: string;
      data: {
        url: string;
        name: string;
        public_host: string;
        access_url: string;
      };
    }>({
      url: '/api/v1/file/upload/presign',
      method: 'POST',
      // headers: {
      //   'Content-Type': 'application/json',
      // },
      body,
    });
  }
  async putUploadAvatar(url: string, data: File, type: string) {
    const u8Array = new Uint8Array(await data.arrayBuffer());
    return this.request({
      url,
      method: 'PUT',
      headers: {
        // Authorization: null,
        'Content-Type': type || 'multipart/form-data',
      },
      body: u8Array,
      encrypt_body: false,
      use_token: false,
    });
  }
  async getUploadAvatar({ file, ...rest }: { file: File } & UploadAvatarPreSignBody) {
    const res = await this.getUploadAvatarPreSign({
      type: file?.name.split('.').pop(),
      category: 'avatar',
      ...rest,
    });
    if (res.code === 1) {
      const { url, access_url } = res.data;
      await this.putUploadAvatar(url, file, file.type);
      return access_url;
    } else {
      throw new Error(res.message);
    }
  }
  usernameCheck(body: { username: string }) {
    return this.request<{
      code: number;
      message: string;
    }>({
      url: '/api/v1/user/username/check',
      method: 'POST',
      body,
    });
  }
  logout(body: { device_id: string }) {
    return this.request({
      url: '/api/v1/user/logout',
      method: 'POST',
      body,
    });
  }
  logOff() {
    return this.request({
      url: '/api/v1/user/cancel',
      method: 'POST',
    });
  }

  devices() {
    return this.request<MyAxiosResponseData<DevicesResponseData>>({
      url: '/api/v1/user/devices',
      method: 'GET',
    });
  }

  devicesForceOffline(device_ids: Array<string>) {
    return this.request<MyAxiosResponseData>({
      url: '/api/v1/user/devices/force/offline',
      method: 'POST',
      body: {
        device_ids,
      },
    });
  }
  getUserAddress(body: { user_id: string }) {
    return this.request<MyAxiosResponseData<UserAddressResponseData>>({
      url: '/api/v1/user/info/address',
      method: 'POST',
      body,
    });
  }
  updateUserAddress(body: { wallet_addresses: Record<string, string> }) {
    return this.request<MyAxiosResponseData>({
      url: '/api/v1/user/auth/wallet',
      method: 'POST',
      body,
    });
  }
  pkeExportPublicKeyPem() {
    return this.client.invoke('pkeExportPublicKeyPem');
  }
  async pkeEncrypt(plainText: string, publicKey?: string) {
    publicKey = publicKey || (await this.pkeExportPublicKeyPem());
    return this.client.invoke('pkeEncrypt', [publicKey, plainText]);
  }
  pkeDecrypt(cipherText: string) {
    return this.client.invoke('pkeDecrypt', [cipherText]);
  }
  async oneSearchByKey(search: string, types?: string[]) {
    return (await this.client.invoke('oneSearchByKey', [search, types])) as unknown as Promise<
      Record<string, any>
    >;
  }

  async searchHistory() {
    return this.oneSearchByKey('', ['history']);
  }

  async delSearchHistory() {
    return await this.client.invoke('deleteAllSearchHistory');
  }

  async postReport(body: any) {
    return await this.request<MyAxiosResponseData>({
      url: '/api/v1/user/illegal-report',
      method: 'POST',
      body,
    });
  }

  async getDeviceLog() {
    const res = await this.client.invoke('getLogDbData');
    return res as unknown as ArrayBuffer;
  }

  async logReport(body: any) {
    return await this.request<MyAxiosResponseData>({
      url: '/api/v1/user/log-report',
      method: 'POST',
      body,
    });
  }

  getUploadUrl(data: { type: string; category: string; name?: string; date_part?: number }) {
    return this.request<{
      code: number;
      message: string;
      data: {
        url: string;
        name: string;
        public_host: string;
        access_url: string;
      };
    }>({
      url: '/api/v1/file/upload/presign',
      method: 'POST',
      body: data,
    });
  }

  async uploadCommon(file: File, category: string) {
    const { type, name } = file;
    const {
      data: { url, access_url },
    } = await this.getUploadUrl({ type, category, name: name.split('.').pop() });

    await this.putUploadAvatar(url, file, file.type);
    return access_url;
  }

  uploadLog(file: File) {
    return this.uploadCommon(file, 'log');
  }
}
